<template>
    <div class="modal-container modal-container--search search-modal" @click.self="handleBackdropClick">
        <div class="modal-inner">
            <div class="modal-dialog modal-dialog--search" role="dialog" aria-modal="true">
                <button class="modal-close" @click="handleClose" aria-label="Close search">
                    <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24">
                        <path d="M256-213.847 213.847-256l224-224-224-224L256-746.153l224 224 224-224L746.153-704l-224 224 224 224L704-213.847l-224-224-224 224Z"/>
                    </svg>
                </button>

                <div class="modal-dialog__content">
                    <!-- Page type filter pills -->
                    <ul
                        class="button-list search-filters"
                        role="radiogroup"
                        aria-label="Filter search results by type"
                    >
                        <li v-for="option in pageTypeOptions" :key="option.value">
                            <input
                                class="search-radio"
                                type="radio"
                                name="page-type-filter"
                                :id="`page-type-${option.value}`"
                                :value="option.value"
                                v-model="selectedPageType"
                            />
                            <label :for="`page-type-${option.value}`">{{ option.label }}</label>
                        </li>
                    </ul>

                    <!-- Search input -->
                    <div class="search-auto-complete" data-float-labels data-float-labels-class-prefix="form-field">
                        <div class="form-field form-field--floated-label">
                            <input
                                id="global-search"
                                type="text"
                                v-model="searchTerm"
                                @keydown.enter.prevent="submitSearch"
                                autocomplete="off"
                            />
                            <label for="global-search">{{ t('placeholder', 'What are you looking for?') }}</label>
                        </div>
                    </div>

                    <!-- State messages -->
                    <div class="search-results__messages">
                        <div v-if="isLoading && results.length === 0" class="search-spinner">
                            <svg class="search-spinner__icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <circle class="search-spinner__track" cx="12" cy="12" r="10" fill="none" stroke-width="3" />
                                <circle class="search-spinner__arc" cx="12" cy="12" r="10" fill="none" stroke-width="3" stroke-linecap="round" />
                            </svg>
                            <span>{{ t('searching', 'Searching…') }}</span>
                        </div>

                        <p v-if="!hasSearched && !isLoading && results.length === 0">
                            {{ t('prompt', 'Start typing to search, or pick a filter to browse.') }}
                        </p>

                        <p v-if="hasSearched && !isLoading && results.length === 0">
                            {{ t('noResults', 'No results found.') }}
                        </p>

                        <p v-if="totalResults > 0 && !isLoading" class="search-results__summary">
                            {{ totalResults }} {{ totalResults === 1 ? t('resultSingular', 'result') : t('resultPlural', 'results') }}<template v-if="lastSearchedTerm.trim()"> {{ t('resultsFor', 'for') }} &ldquo;{{ lastSearchedTerm }}&rdquo;</template>.
                        </p>
                    </div>

                    <!-- Results -->
                    <div v-if="results.length" class="search-results">
                        <a
                            class="search-result"
                            v-for="(result, i) in results"
                            :key="result.url || i"
                            :href="result.url || '#'"
                            :target="result.pageType === 'Office' ? '_blank' : null"
                            :rel="result.pageType === 'Office' ? 'noopener' : null"
                        >
                            <div v-if="result.imageUrl" class="search-result__image-clip">
                                <img
                                    class="search-result__image"
                                    :src="result.imageUrl"
                                    :alt="result.name || 'Search result'"
                                    loading="lazy"
                                />
                            </div>

                            <div class="search-result__content">
                                <p class="search-result__title">
                                    <span v-if="result.pageType" class="search-result__tag badge">{{ tagLabel(result.pageType) }}</span>
                                    <span v-html="highlightMatch(result.name)"></span>
                                </p>
                                <p v-if="result.articleDate" class="search-result__date"><strong>{{ result.articleDate }}</strong></p>
                                <p v-if="result.company" class="search-result__meta"><strong>{{ t('company', 'Company') }}:</strong> {{ result.company }}</p>
                                <p v-if="result.locations" class="search-result__meta"><strong>{{ t('locations', 'Locations') }}:</strong> {{ result.locations }}</p>
                                <p v-if="result.postedDate" class="search-result__meta"><strong>{{ t('posted', 'Posted') }}:</strong> {{ result.postedDate }}</p>
                                <p v-if="result.description" class="search-result__description" v-html="highlightMatch(result.description)"></p>
                                <span class="button-link" aria-hidden="true">{{ result.pageType === 'Office' ? t('directions', 'Get directions') : t('readMore', 'Read more') }}</span>
                            </div>
                        </a>
                    </div>

                    <!-- Infinite scroll sentinel -->
                    <div
                        v-if="canLoadMore"
                        ref="loadMoreTrigger"
                        class="search-results__sentinel"
                        aria-hidden="true"
                    ></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
const GetHttp = () => import(/* webpackChunkName: 'http' */ '../../_http').then(v => v.default);

import { ref, computed, onMounted, onUpdated, onUnmounted, watch, nextTick } from 'vue';
import { floatLabels } from '../../partials/_float-labels';

const MIN_CHARS = 3;

const props = defineProps({
    rootId: {
        type: Number,
        required: true
    },
    labels: {
        type: Object,
        default: () => ({})
    },
    availableFilters: {
        type: Array,
        default: null
    }
});

const emit = defineEmits(['close']);

// Dictionary-backed label with an English fallback.
const t = (key, fallback) => {
    const v = props.labels && props.labels[key];
    return (v === undefined || v === null || v === '') ? fallback : v;
};

const pageTypeOptions = computed(() => {
    const optional = [
        { label: t('news', 'News'),       value: 'news' },
        { label: t('jobs', 'Jobs'),       value: 'jobs' },
        { label: t('offices', 'Offices'), value: 'offices' }
    ];

    // Backend passes which of these the site actually has content for; hide the rest.
    // (null = not provided → show all as a safe fallback.) "All" is always shown.
    const filtered = Array.isArray(props.availableFilters)
        ? optional.filter(o => props.availableFilters.includes(o.value))
        : optional;

    return [{ label: t('all', 'All'), value: 'all' }, ...filtered];
});

// Translated display for the result type tag (raw result.pageType stays for logic).
const tagLabel = (pageType) => {
    switch (pageType) {
        case 'News':   return t('tagNews', 'News');
        case 'Job':    return t('tagJob', 'Job');
        case 'Office': return t('tagOffice', 'Office');
        case 'Page':   return t('tagPage', 'Page');
        default:       return pageType;
    }
};

const http = ref(null);
const searchTerm = ref('');
const lastSearchedTerm = ref('');
const selectedPageType = ref('all');
const results = ref([]);
const totalPages = ref(1);
const currentPage = ref(0);
const totalResults = ref(0);
const hasSearched = ref(false);
const isLoading = ref(false);

const ensureHttp = async () => {
    if (!http.value) {
        http.value = await GetHttp();
    }
};

const hasActiveFilter = computed(() => selectedPageType.value && selectedPageType.value !== 'all');

const canLoadMore = computed(() =>
    !isLoading.value &&
    results.value.length > 0 &&
    currentPage.value < totalPages.value
);

/* Normalise a result regardless of API casing (camelCase vs PascalCase). */
const normalise = r => ({
    name: r.name ?? r.Name ?? '',
    description: r.description ?? r.Description ?? '',
    url: r.url ?? r.Url ?? '',
    pageType: r.pageType ?? r.PageType ?? '',
    imageUrl: r.imageUrl ?? r.ImageUrl ?? '',
    articleDate: r.articleDate ?? r.ArticleDate ?? '',
    company: r.company ?? r.Company ?? '',
    locations: r.locations ?? r.Locations ?? '',
    postedDate: r.postedDate ?? r.PostedDate ?? ''
});

/* Highlight helpers */
const escapeHtml = (str = '') =>
    String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

const escapeRegExp = (str = '') => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightMatch = text => {
    const term = lastSearchedTerm.value.trim();
    const source = text || '';

    if (!term || term.length < MIN_CHARS || !source) {
        return escapeHtml(source);
    }

    const regex = new RegExp(`(${escapeRegExp(term)})`, 'gi');
    return escapeHtml(source).replace(regex, '<span class="search-result__highlight">$1</span>');
};

/* Infinite scroll */
const loadMoreTrigger = ref(null);
let loadMoreObserver = null;

const disconnectObserver = () => {
    if (loadMoreObserver) {
        loadMoreObserver.disconnect();
        loadMoreObserver = null;
    }
};

const createObserver = () => {
    if (!loadMoreTrigger.value || !canLoadMore.value) {
        disconnectObserver();
        return;
    }

    disconnectObserver();

    loadMoreObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (entry.isIntersecting && canLoadMore.value) {
                loadMore();
            }
        });
    }, { root: null, threshold: 0.25 });

    loadMoreObserver.observe(loadMoreTrigger.value);
};

const resetResults = () => {
    hasSearched.value = false;
    results.value = [];
    totalResults.value = 0;
    totalPages.value = 1;
    currentPage.value = 0;
    lastSearchedTerm.value = '';
    isLoading.value = false;
    disconnectObserver();
};

const performSearch = async (page = 1, explicitTerm) => {
    if (isLoading.value && page === 1) return;

    const term = (explicitTerm ?? searchTerm.value).trim();
    if (!term && !hasActiveFilter.value) return;

    hasSearched.value = true;
    isLoading.value = true;

    try {
        await ensureHttp();

        const { data } = await http.value.get('/umbraco/api/search/globalsearch', {
            params: {
                query: term,
                page,
                rootId: props.rootId,
                pageType: selectedPageType.value || 'all'
            }
        });

        const rawItems = data.items ?? data.Items ?? [];
        const items = rawItems.map(normalise);

        if (page === 1) {
            results.value = items;
        } else {
            results.value.push(...items);
        }

        totalPages.value = data.totalPages ?? data.TotalPages ?? 1;
        totalResults.value = data.totalResults ?? data.TotalResults ?? 0;
        currentPage.value = page;
        lastSearchedTerm.value = data.searchTerm ?? data.SearchTerm ?? term;

        await nextTick();
        createObserver();
    } catch {
        // Fail quietly — leave any existing results in place.
    } finally {
        isLoading.value = false;
    }
};

const loadMore = () => {
    if (!canLoadMore.value) return;
    performSearch(currentPage.value + 1, lastSearchedTerm.value || searchTerm.value);
};

const submitSearch = () => {
    const term = searchTerm.value.trim();
    if ((!term || term.length < MIN_CHARS) && !hasActiveFilter.value) return;
    performSearch(1, term);
};

const handleClose = () => {
    resetResults();
    searchTerm.value = '';
    selectedPageType.value = 'all';
    emit('close');
};

const hasTextSelection = () => {
    if (typeof window === 'undefined' || !window.getSelection) return false;
    const sel = window.getSelection();
    return sel && sel.toString().trim().length > 0;
};

const handleBackdropClick = () => {
    if (hasTextSelection()) return;
    handleClose();
};

const onKeydown = event => {
    if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        handleClose();
    }
};

/* Debounced live search on input */
let debounceTimeout = null;

watch(searchTerm, newVal => {
    if (debounceTimeout) clearTimeout(debounceTimeout);

    const term = newVal.trim();

    if ((!term || term.length < MIN_CHARS) && !hasActiveFilter.value) {
        resetResults();
        return;
    }

    debounceTimeout = setTimeout(() => {
        if (term === lastSearchedTerm.value && !hasActiveFilter.value) return;
        performSearch(1, term);
    }, 500);
});

/* Re-run when the filter pill changes */
watch(selectedPageType, () => {
    const term = searchTerm.value.trim();

    if ((!term || term.length < MIN_CHARS) && !hasActiveFilter.value) {
        resetResults();
        return;
    }

    disconnectObserver();
    results.value = [];
    totalPages.value = 1;
    currentPage.value = 0;
    performSearch(1, term);
});

watch([canLoadMore, loadMoreTrigger], () => {
    nextTick().then(createObserver);
});

onMounted(async () => {
    await nextTick();
    floatLabels();
    window.addEventListener('keydown', onKeydown);
});

onUpdated(() => {
    floatLabels();
});

onUnmounted(() => {
    disconnectObserver();
    if (debounceTimeout) clearTimeout(debounceTimeout);
    window.removeEventListener('keydown', onKeydown);
});
</script>
